import { facilityDisplayName } from 'utils/campusLabels';

/** Task App facility filter for a multi-facility campus: what's selected, and which tasks it keeps. */

/** The person's pick, tagged with the campus it was made in so it never carries to another campus. */
export interface FacilityFilterChoice {
  campusKey: string;
  ids: string[];
}

export interface FacilityFilter {
  /** Identifies the campus; tag a new pick with it. */
  campusKey: string;
  /** Every campus facility id: the "Show all campus tasks" selection. */
  allIds: string[];
  /** Selected facility ids. */
  ids: string[];
  /** True when the selection hides some campus facilities. */
  isFiltered: boolean;
  /** Selected facilities' names, comma separated. */
  names: string;
}

/**
 * The selection: the person's pick for this campus, else their own facilities
 * (from locations_users) within it, else the whole campus.
 */
export function resolveFacilityFilter(
  campusFacilities: Array<{ id: string | number; name: string; facility_alias?: string | null }>,
  userFacilityIds: Array<string | number>,
  choice: FacilityFilterChoice | null
): FacilityFilter {
  const allIds = campusFacilities.map((f) => String(f.id));
  const campusKey = allIds.join(',');
  const userIds = new Set(userFacilityIds.map(String));
  const ownIds = allIds.filter((id) => userIds.has(id));
  const defaultIds = ownIds.length > 0 ? ownIds : allIds;
  const ids = choice?.campusKey === campusKey ? choice.ids : defaultIds;

  return {
    campusKey,
    allIds,
    ids,
    isFiltered: allIds.length > 1 && ids.length < allIds.length,
    names: ids
      .map((id) => facilityDisplayName(campusFacilities.find((f) => String(f.id) === id)) || id)
      .join(', ')
  };
}

/** A task is kept when it is filed under, or moves a trailer out of, a selected facility. */
export function taskMatchesFacilityFilter(
  task: {
    facility?: { id: string | number } | null;
    facility_id?: string | number | null;
    source_facility_id?: string | number | null;
  },
  ids: string[]
): boolean {
  return [task.facility?.id ?? task.facility_id, task.source_facility_id].some(
    (id) => id != null && ids.includes(String(id))
  );
}
